import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppAuth } from '../hooks/useAppAuth';
import { useRole } from '../hooks/useRole';
import { createApiClient, disputeService } from '../services/api';
import { DisputeStatus, DisputeCategory, DisputeResolutionType } from '../types/modules';

const STATUS_LABELS: Record<DisputeStatus, string> = {
  [DisputeStatus.Open]: 'Open',
  [DisputeStatus.InReview]: 'In Review',
  [DisputeStatus.Proposed]: 'Resolution Proposed',
  [DisputeStatus.Resolved]: 'Resolved',
  [DisputeStatus.Escalated]: 'Escalated',
  [DisputeStatus.Closed]: 'Closed',
  [DisputeStatus.Withdrawn]: 'Withdrawn',
};
const STATUS_COLOURS: Record<DisputeStatus, string> = {
  [DisputeStatus.Open]: 'bg-red-100 text-red-800',
  [DisputeStatus.InReview]: 'bg-amber-100 text-amber-800',
  [DisputeStatus.Proposed]: 'bg-blue-100 text-blue-800',
  [DisputeStatus.Resolved]: 'bg-green-100 text-green-800',
  [DisputeStatus.Escalated]: 'bg-orange-100 text-orange-800',
  [DisputeStatus.Closed]: 'bg-gray-100 text-gray-700',
  [DisputeStatus.Withdrawn]: 'bg-gray-100 text-gray-700',
};
const CATEGORY_LABELS: Record<DisputeCategory, string> = {
  [DisputeCategory.ItemDamaged]: 'Item Damaged',
  [DisputeCategory.ItemMissing]: 'Item Missing',
  [DisputeCategory.LateDelivery]: 'Late Delivery',
  [DisputeCategory.CarrierNoShow]: 'Carrier No-Show',
  [DisputeCategory.IncorrectDelivery]: 'Incorrect Delivery',
  [DisputeCategory.PaymentDisputed]: 'Payment Disputed',
  [DisputeCategory.FraudSuspected]: 'Fraud Suspected',
  [DisputeCategory.VehicleRepairQuality]: 'Repair Quality',
  [DisputeCategory.RelayHandoverFailed]: 'Relay Handover Failed',
  [DisputeCategory.Other]: 'Other',
};
const RESOLUTION_LABELS: Record<DisputeResolutionType, string> = {
  [DisputeResolutionType.FullRefundToCustomer]: 'Full Refund to Customer',
  [DisputeResolutionType.PartialRefundToCustomer]: 'Partial Refund',
  [DisputeResolutionType.NoRefund]: 'No Refund',
  [DisputeResolutionType.CarrierPenalty]: 'Carrier Penalty Applied',
  [DisputeResolutionType.MutualAgreement]: 'Mutual Agreement',
  [DisputeResolutionType.EscalatedToLegal]: 'Escalated to Legal',
};

export default function AdminDisputesPage() {
  const { getToken } = useAppAuth();
  const { role, isAdminOrOps, isRoleLoading } = useRole();
  const isStrictAdmin = role === 'admin';
  const qc = useQueryClient();

  const { data: disputes = [], isLoading } = useQuery({
    queryKey: ['admin-disputes-queue'],
    queryFn: () => disputeService(createApiClient(getToken)).getQueue(),
    enabled: isAdminOrOps,
  });

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [resolutionType, setResolutionType] = useState<DisputeResolutionType>(DisputeResolutionType.FullRefundToCustomer);
  const [refundAmount, setRefundAmount] = useState('');
  const [explanation, setExplanation] = useState('');

  const proposeMutation = useMutation({
    mutationFn: (disputeId: string) =>
      disputeService(createApiClient(getToken)).proposeResolution(disputeId, {
        resolutionType,
        refundAmount: refundAmount ? Number(refundAmount) : undefined,
        explanation,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-disputes-queue'] });
      setExpandedId(null);
      setExplanation('');
      setRefundAmount('');
    },
    onError: (e: any) => alert(e.message ?? 'Failed to propose resolution.'),
  });

  const escalateMutation = useMutation({
    mutationFn: (disputeId: string) => disputeService(createApiClient(getToken)).escalate(disputeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-disputes-queue'] }),
    onError: (e: any) => alert(e.message ?? 'Failed to escalate.'),
  });

  if (isRoleLoading) return <div className="text-center py-10 text-gray-400">Loading…</div>;
  if (!isAdminOrOps) return <div className="text-center py-10 text-gray-500">Access restricted to Admin and Ops users.</div>;

  return (
    <div className="page-shell-tight">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-900">Disputes</h1>
        <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 text-gray-700">
          {disputes.length} open dispute{disputes.length === 1 ? '' : 's'}
        </span>
      </div>

      {isLoading ? (
        <div className="text-center py-10 text-gray-400">Loading…</div>
      ) : disputes.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-16">No open disputes.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {disputes.map(d => {
            const expanded = expandedId === d.id;
            return (
              <div key={d.id} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                <button
                  className="w-full text-left"
                  onClick={() => setExpandedId(expanded ? null : d.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-gray-900 text-sm">{d.title}</p>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_COLOURS[d.status]}`}>
                      {STATUS_LABELS[d.status]}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    #{d.referenceNumber} · {CATEGORY_LABELS[d.category]} · Raised {new Date(d.createdAt).toLocaleDateString('en-GB')}
                  </p>
                </button>

                {expanded && (
                  <div className="mt-3 border-t border-gray-100 pt-3">
                    <p className="text-sm text-gray-700 mb-3">{d.description}</p>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">Resolution</label>
                        <select
                          value={resolutionType}
                          onChange={(e) => setResolutionType(Number(e.target.value))}
                          className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                        >
                          {Object.entries(RESOLUTION_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">Refund Amount (£, optional)</label>
                        <input
                          type="number"
                          value={refundAmount}
                          onChange={(e) => setRefundAmount(e.target.value)}
                          className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                        />
                      </div>
                    </div>

                    <label className="text-xs font-medium text-gray-600 block mb-1">Explanation</label>
                    <textarea
                      value={explanation}
                      onChange={(e) => setExplanation(e.target.value)}
                      rows={3}
                      className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 mb-3"
                    />

                    <div className="flex gap-2">
                      <button
                        onClick={() => proposeMutation.mutate(d.id)}
                        disabled={!explanation.trim() || proposeMutation.isPending || !isStrictAdmin}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-50"
                      >
                        {d.status === DisputeStatus.Proposed ? 'Update Resolution Proposal' : 'Propose Resolution'}
                      </button>
                      <button
                        onClick={() => escalateMutation.mutate(d.id)}
                        disabled={escalateMutation.isPending || !isStrictAdmin}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-orange-300 text-orange-700 disabled:opacity-50"
                      >
                        Escalate
                      </button>
                    </div>
                    {!isStrictAdmin && (
                      <p className="text-xs text-amber-700 mt-2">Only Admin users (not Ops) can propose resolutions or escalate.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
